const SET_TYPE_LABEL_KEYS = {
  work: 'workout.setType.work.label',
  warmup: 'workout.setType.warmup.label',
  failure: 'workout.setType.failure.label',
  drop: 'workout.setType.drop.label',
  backoff: 'workout.setType.backoff.label',
};

const SET_TYPE_FALLBACK = {
  work: 'Work Set',
  warmup: 'Warm-up [W]',
  failure: 'To Failure',
  drop: 'Drop Set [D]',
  backoff: 'Back-off [B]',
};

function setTypeLabel(type, t) {
  if (SET_TYPE_LABEL_KEYS[type]) {
    return t(SET_TYPE_LABEL_KEYS[type], SET_TYPE_FALLBACK[type]);
  }
  if (!type) return SET_TYPE_FALLBACK.work;
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function repsString(set) {
  const lo = set.target_reps_min;
  const hi = set.target_reps_max;
  if (lo != null && hi != null) {
    return lo === hi ? String(lo) : `${lo}–${hi}`;
  }
  if (lo != null) return String(lo);
  if (set.reps != null) return String(set.reps);
  return '?';
}

function setSignature(set, t) {
  const label = setTypeLabel(set.set_type, t);
  if (set.target_duration_seconds) {
    return { label, body: `${Math.round(set.target_duration_seconds / 60)} min` };
  }
  const reps = repsString(set);
  let suffix = '';
  if (set.target_weight != null && set.target_weight !== 0 && set.target_weight !== '') {
    suffix += ` @ ${set.target_weight}kg`;
  }
  if (set.target_rir != null && set.target_rir !== '') {
    suffix += ` RIR ${set.target_rir}`;
  }
  return { label, body: `${reps}${suffix}` };
}

function groupSets(sets, t) {
  // Walk in order; merge consecutive sets that share label+body into "N× body".
  const groups = [];
  for (const s of sets) {
    const sig = setSignature(s, t);
    const last = groups[groups.length - 1];
    if (last && last.label === sig.label && last.body === sig.body) {
      last.count += 1;
    } else {
      groups.push({ label: sig.label, body: sig.body, count: 1 });
    }
  }
  return groups;
}

function renderExercise(ex, idx, t) {
  const lines = [];
  lines.push(`${idx + 1}. **${ex.name || '(unnamed)'}**`);
  const groups = groupSets(ex.sets || [], t);
  // If consecutive groups share a label, list all under one bullet (e.g., Warmup: 1× 10–15, 1× 5–8).
  // Achieved by collapsing same-label adjacency:
  const collapsed = [];
  for (const g of groups) {
    const last = collapsed[collapsed.length - 1];
    if (last && last.label === g.label) {
      last.entries.push(`${g.count}× ${g.body}`);
    } else {
      collapsed.push({ label: g.label, entries: [`${g.count}× ${g.body}`] });
    }
  }
  for (const c of collapsed) {
    lines.push(`   - ${c.label}: ${c.entries.join(', ')}`);
  }
  if (ex.rest_seconds) {
    lines.push(`   - ${t('routineShare.format.rest', 'Rest')}: ${ex.rest_seconds}s`);
  }
  if (ex.notes && ex.notes.trim()) {
    lines.push(`   - *${ex.notes.trim()}*`);
  }
  return lines.join('\n');
}

export function formatRoutineAsText(routine, t) {
  const out = [];
  out.push(`# ${routine.name || '(unnamed routine)'}`);
  if (routine.description && routine.description.trim()) {
    out.push('');
    out.push(routine.description.trim());
  }
  if (routine.difficulty) {
    out.push('');
    out.push(`**${t('routineShare.format.difficulty', 'Difficulty')}:** ${routine.difficulty}`);
  }
  out.push('');
  out.push('---');

  const days = routine.days || [];
  if (days.length === 0) {
    out.push('');
    out.push(t('routineShare.format.noDays', '(no days)'));
  } else {
    days.forEach((day, i) => {
      out.push('');
      const dayLabel = t('routineShare.format.day', 'Day');
      const dayName = day.day_name || day.name || `${dayLabel} ${i + 1}`;
      out.push(`## ${dayLabel} ${i + 1} — ${dayName}`);
      out.push('');
      const exs = day.exercises || [];
      exs.forEach((ex, j) => {
        out.push(renderExercise(ex, j, t));
        if (j < exs.length - 1) out.push('');
      });
    });
  }

  out.push('');
  out.push('---');
  out.push('');
  out.push(`_${t('routineShare.format.footer', 'Shared from [trainedbyrs.com](https://trainedbyrs.com)')}_`);

  return out.join('\n');
}
